import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';
import { FiArrowLeft, FiPlus, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const ModalOverlay = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:rgb(236, 233, 233);
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  background: #f8f8f8;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  padding: 8px;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  color: #333;
  margin-left: 8px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

const ImageGallery = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px;
  background: #f8f8f8;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ImageLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ProductImage = styled.img`
  width: 110px;
  height: 110px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Content = styled.div`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  position: relative;
  z-index: 1;
`;

const Section = styled.div`
  margin-bottom: 25px;
  background: white;
  border-radius: 12px;
  overflow: visible;
  border: 1px solid rgba(207, 49, 49, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
`;

const SectionTitle = styled.h3<{ color?: string }>`
  font-size: 14px;
  color: ${props => props.color || '#ff0000'};
  margin-bottom: 0;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
  padding: 15px;
  background: rgba(255, 0, 0, 0.03);
  border-bottom: 1px solid rgba(207, 49, 49, 0.1);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 15px;
  background: white;
  position: relative;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f8f8f8;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color:rgb(250, 154, 154);
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: #333;
  min-height: 20px;
  display: flex;
  align-items: center;
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  padding: 15px;
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const Button = styled(m.button)<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${props => props.variant === 'primary' ? '#ff0000' : '#f8f8f8'};
  color: ${props => props.variant === 'primary' ? 'white' : '#333'};
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
  z-index: 1000;
`;

const SelectInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: 'Poppins', sans-serif;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const DropdownList = styled.ul<{ show: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  display: ${props => props.show ? 'block' : 'none'};
  padding: 0;
  list-style: none;
  margin: 0;
`;

const DropdownItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  list-style: none;
  background: white;
  
  &:hover {
    background: #f8f8f8;
  }

  &.add-new {
    color: #ff0000;
    display: flex;
    align-items: center;
    gap: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }
`;

const FullScreenImage = styled(m.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const FullScreenImageContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationControls = styled.div`
  position: absolute;
  top: 180px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  z-index: 2001;
`;

const NavigationButton = styled(m.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 18px;
  }
`;

const FullScreenImageView = styled(m.img)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const FullScreenImageClose = styled(m.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  z-index: 2001;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px;
    font-size: 20px;
  }
`;

const StockInfoSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: 'Poppins', sans-serif;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const LocationGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LocationInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  outline: none;
  font-family: 'Poppins', sans-serif;
  min-width: 0;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
  }
`;

const RefInput = styled(LocationInput)`
  width: 120px;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const QuestionMark = styled.span`
  color: #666;
  cursor: help;
  margin: 0 5px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    align-self: flex-end;
    margin: -5px 0;
  }
`;

interface ViewProductProps {
  onClose: () => void;
  onShortlist: () => void;
  productData: {
    code: string;
    images: string[];
    details: {
      technical: {
        material: string;
        colorTexture: string;
        variation: string;
        repetition: string;
        recommendation: string;
        traffic: string;
        edgean: string;
        spacer: string;
        recommendedAdhesive: string;
        recommendedGrout: string;
        antiSlip: string;
      };
      size: {
        length: string;
        width: string;
        tesseraSize: string;
        thickness: string;
        dia: string;
      };
      options: {
        colorOption: string;
        sizeOption: string;
      };
    };
  };
}

const ViewProduct: React.FC<ViewProductProps> = ({ onClose, onShortlist, productData }) => {
  const [roomName, setRoomName] = useState('Karthik\'s room');
  const [area, setArea] = useState('');
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const [roomOptions, setRoomOptions] = useState<string[]>([]);
  const [areaOptions] = useState<string[]>([
    'Wall',
    'Floor',
    'Wall & Floor',
    'Ceiling',
    'Countertop',
    'Backsplash',
    'Shower',
    'Exterior'
  ]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [itemStatus, setItemStatus] = useState('S2S');
  const [location, setLocation] = useState('Surface Warehouse');
  const [displayRef, setDisplayRef] = useState('');
  const [ref, setRef] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  useEffect(() => {
    // Load saved room names from localStorage
    const savedRooms = localStorage.getItem('roomNames');
    if (savedRooms) {
      setRoomOptions(JSON.parse(savedRooms));
    } else {
      setRoomOptions(['Karthik\'s room']);
    }
  }, []);

  const handleRoomSelect = (room: string) => {
    setRoomName(room);
    setShowRoomDropdown(false);
    
    if (!roomOptions.includes(room)) {
      const newOptions = [...roomOptions, room];
      setRoomOptions(newOptions);
      localStorage.setItem('roomNames', JSON.stringify(newOptions));
    }
  };

  const handleAreaSelect = (area: string) => {
    setArea(area);
    setShowAreaDropdown(false);
  };

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRoomDropdown || showAreaDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('.custom-select')) {
          setShowRoomDropdown(false);
          setShowAreaDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRoomDropdown, showAreaDropdown]);

  const handlePrevImage = () => {
    setSlideDirection('left');
    setSelectedImageIndex((prev) => 
      prev === 0 ? productData.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSlideDirection('right');
    setSelectedImageIndex((prev) => 
      prev === productData.images.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      <Header>
        <BackButton onClick={onClose}>
          <FiArrowLeft />
        </BackButton>
        <HeaderTitle>View Product</HeaderTitle>
      </Header>

      <ImageGallery>
        {productData.images.map((image, index) => (
          <ImageLink 
            key={index} 
            onClick={(e) => {
              e.preventDefault();
              setSelectedImageIndex(index);
              setSelectedImage(image);
            }}
          >
            <ProductImage src={image} alt={`Product ${index + 1}`} />
          </ImageLink>
        ))}
      </ImageGallery>

      <Content>
        <Section>
          <SectionTitle color="#ff0000">ROOM INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Room Name</InfoLabel>
              <CustomSelect className="custom-select">
                <SelectInput
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  onFocus={() => {
                    setShowAreaDropdown(false);
                    setShowRoomDropdown(true);
                  }}
                  placeholder="Enter room name"
                />
                <DropdownList show={showRoomDropdown}>
                  {roomOptions.map((room, index) => (
                    <DropdownItem
                      key={index}
                      onClick={() => handleRoomSelect(room)}
                    >
                      {room}
                    </DropdownItem>
                  ))}
                  {roomName && !roomOptions.includes(roomName) && (
                    <DropdownItem
                      className="add-new"
                      onClick={() => handleRoomSelect(roomName)}
                    >
                      <FiPlus /> Add "{roomName}"
                    </DropdownItem>
                  )}
                </DropdownList>
              </CustomSelect>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Application</InfoLabel>
              <StockInfoSelect 
                value={area}
                onChange={(e) => setArea(e.target.value)}
              >
                <option value="">Select Application</option>
                {areaOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </StockInfoSelect>
            </InfoRow>
          </InfoGrid>
        </Section>


        <Section>
          <SectionTitle color="#ff0000">PRODUCT INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>SB Item Code</InfoLabel>
              <InfoValue>{productData.code}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Rate/Pc</InfoLabel>
              <InfoValue>-</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Material</InfoLabel>
              <InfoValue>{productData.details.technical.material}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle color="#ff0000">STOCK INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Item Status</InfoLabel>
              <InfoValue>{itemStatus}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>{location}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Display Reference</InfoLabel>
              <InfoValue>
                {displayRef} {ref && `(Ref: ${ref})`}
              </InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle color="#ff0000">TECHNICAL INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Colour/Texture</InfoLabel>
              <InfoValue>{productData.details.technical.colorTexture}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Variation</InfoLabel>
              <InfoValue>{productData.details.technical.variation}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Repetition</InfoLabel>
              <InfoValue>{productData.details.technical.repetition}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Recommendation</InfoLabel>
              <InfoValue>{productData.details.technical.recommendation}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Traffic</InfoLabel>
              <InfoValue>{productData.details.technical.traffic}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Edgean</InfoLabel>
              <InfoValue>{productData.details.technical.edgean}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Spacer</InfoLabel>
              <InfoValue>{productData.details.technical.spacer}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Recommended Adhesive</InfoLabel>
              <InfoValue>{productData.details.technical.recommendedAdhesive}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Recommended Grout</InfoLabel>
              <InfoValue>{productData.details.technical.recommendedGrout}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Anti slip</InfoLabel>
              <InfoValue>{productData.details.technical.antiSlip}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle color="#ff0000">SIZE INFO</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Length</InfoLabel>
              <InfoValue>{productData.details.size.length}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Width</InfoLabel>
              <InfoValue>{productData.details.size.width}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Tessera Size</InfoLabel>
              <InfoValue>{productData.details.size.tesseraSize}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Thickness</InfoLabel>
              <InfoValue>{productData.details.size.thickness}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Dia</InfoLabel>
              <InfoValue>{productData.details.size.dia}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle color="#ff0000">OPTION</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>Colour option</InfoLabel>
              <InfoValue>{productData.details.options.colorOption}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Size option</InfoLabel>
              <InfoValue>{productData.details.options.sizeOption}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </Section>
      </Content>

      {selectedImage && (
        <FullScreenImage
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedImage(null)}
        >
          <FullScreenImageContent onClick={(e) => e.stopPropagation()}>
            <FullScreenImageClose 
              onClick={() => setSelectedImage(null)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </FullScreenImageClose>
            
            <NavigationControls>
              <NavigationButton
                onClick={handlePrevImage}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiChevronLeft />
              </NavigationButton>
              <NavigationButton
                onClick={handleNextImage}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiChevronRight />
              </NavigationButton>
            </NavigationControls>

            <FullScreenImageView
              src={productData.images[selectedImageIndex]}
              alt="Full screen product"
              key={selectedImageIndex}
              initial={{ 
                scale: 0.8, 
                opacity: 0,
                rotateY: slideDirection === 'right' ? 45 : -45 
              }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                rotateY: 0 
              }}
              exit={{ 
                scale: 0.8, 
                opacity: 0,
                rotateY: slideDirection === 'right' ? -45 : 45 
              }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 25,
                mass: 1
              }}
            />
          </FullScreenImageContent>
        </FullScreenImage>
      )}

      <ButtonGroup>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={onShortlist}>Shortlist</Button>
      </ButtonGroup>
    </ModalOverlay>
  );
};

export default ViewProduct; 